import { hasLiveFiskalDocument, tt } from '@agos/shared'

/**
 * A finished order whose fiscal document and status disagree - `FRONTEND.md` §9.573, FB-866.
 *
 * **The owner's two sentences**, and both move money.
 *
 * **`missing`** is a delivered order with no live document: food left the kitchen and the register never
 * registered the sale. `FiskalClosureService.takingsSince` counts the drawer against `fiskal_printed_at
 * IS NOT NULL AND fiskal_storno_at IS NULL`, so the till holds money the uzávierka does not expect -
 * FB-863 measures that gap per handover and said nothing per order.
 *
 * **`cancelled-but-printed`** is the mirror and worse: the register has registered a sale that did not
 * happen. That document is on the Z-report and in the register's own fiscal memory, so the day's declared
 * takings are higher than what was taken and nothing withdraws it but a storno.
 */
export type FiskalMismatch = 'missing' | 'cancelled-but-printed'

/**
 * Only the two statuses the owner named - §9.573.
 *
 * <p>**`new`, `preparing`, `ready`, `quality_check`, `on_the_way` and `at_address` are deliberately
 * silent.** An order still being cooked has not been paid for, so *no document yet* is the ordinary
 * state there and a warning would fire on every open order - which is how a warning stops being read.
 * The rule is about an order that has **finished**, one way or the other.
 */
const DELIVERED = 'delivered'
const CANCELLED = 'cancelled'

/**
 * What disagrees, or null - §9.573.
 *
 * <p>**A named outcome rather than a boolean**, because the two states need different sentences: one says
 * money is in the till the closure will not expect, the other that the register is holding a sale that
 * did not happen. A boolean plus one string would make the badge say the wrong one of two true things -
 * §9.538's shape.
 *
 * <p>**Both stamps, through {@link hasLiveFiskalDocument}.** §10A.185 clears the storno stamp on a
 * re-issue, so an order printed, voided and printed again **has** a live document and must not warn.
 * Reading `fiskalStornoAt` alone would warn on exactly the orders somebody has already put right - which
 * is the *"and not reprinted"* half of what the owner asked for.
 */
export function fiskalMismatch(order: {
  status?: string | null
  fiskalPrintedAt?: string | null
  fiskalStornoAt?: string | null
}): FiskalMismatch | null {
  const status = order.status ?? ''
  const live = hasLiveFiskalDocument(order)
  if (status === DELIVERED && !live) return 'missing'
  if (status === CANCELLED && live) return 'cancelled-but-printed'
  return null
}

/** What the warning says, so an operator knows which way the money is wrong - §9.573. */
export function fiskalMismatchNote(mismatch: FiskalMismatch): string {
  if (mismatch === 'missing') {
    return tt(
      'Objednávka je doručená, ale nemá platný fiškálny doklad - doklad nebol vytlačený alebo bol stornovaný a znovu sa nevytlačil. Uzávierka túto sumu nepočíta.',
    )
  }
  return tt(
    'Objednávka je zrušená, ale fiškálny doklad je stále platný - pokladňa má zaevidovaný predaj, ktorý sa nestal. Doklad treba stornovať.',
  )
}
